import { DateTime } from 'luxon';
import { JobSignatureVersion } from '../models/job-signature-version';
import { RecurrenceState } from '../models/recurrence-state';
import { RunAtOutOfRangeError } from '../models/run-at-out-of-range-error';
import { arrayBufferToBase64, convertSecondsToTimeSpan, isJsonResponse, isValidTimeZone } from '../utilities/utilities';
import { HttpClient } from './http-client.service';
import { ZeroTrustService } from './zero-trust.service';
import { TimeIsOutOfSyncError } from '../models/time-is-out-of-sync-error';
export class RemoteExecutionService {
    constructor(http, zeroTrustService, logger) {
        this.http = http;
        this.zeroTrustService = zeroTrustService;
        this.logger = logger;
        this.defaultTaskTimeout = 3600;
        this.defaultRunTimeout = 86400;
    }
    static create(uiFrameAdapter, logger, options) {
        const { remoteExecutionFrontendApiUrl, zeroTrustApiUrl } = options;
        const { accessTokenFactory, clientComponent } = uiFrameAdapter;
        const remoteExecutionHttp = new HttpClient(remoteExecutionFrontendApiUrl, accessTokenFactory, clientComponent);
        const zeroTrustService = new ZeroTrustService(zeroTrustApiUrl, uiFrameAdapter, logger);
        return new RemoteExecutionService(remoteExecutionHttp, zeroTrustService, logger);
    }
    /**
     * Runs a Remote Execution job.
     * @param job - The job to be run.
     * @param modalText - The text Zero Trust sign flow modal displays to the user.
     * @returns The successfully created run.
     * */
    async run(job, modalText) {
        var _a, _b;
        const { displayName, steps, devices: hosts, platform, scheduleDateTime, app, taskTimeout, recurrenceOptions } = job;
        const isScheduled = !!scheduleDateTime || !!recurrenceOptions;
        const effectiveScheduleDateTime = (_b = (_a = recurrenceOptions === null || recurrenceOptions === void 0 ? void 0 : recurrenceOptions.recurrenceStartDate) !== null && _a !== void 0 ? _a : scheduleDateTime) !== null && _b !== void 0 ? _b : new Date();
        try {
            // use browser's time zone in case it's null on the host snapshot, required for scheduling purposes
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const hostsWithTimezone = hosts.map(host => { var _a; return ({ ...host, timeZone: (_a = host.timeZone) !== null && _a !== void 0 ? _a : browserTimeZone }); });
            if (hostsWithTimezone.some(host => !isValidTimeZone(host.timeZone))) {
                throw new Error(`Some device have invalid time zone: ${[
                    ...new Set(hostsWithTimezone.filter(host => !isValidTimeZone(host.timeZone)).map(host => host.timeZone))
                ]}`);
            }
            // get all step schemas and map job's steps to them
            const schemas = await this.getStepSchemas();
            const stepSchemas = steps.map(step => {
                const schema = schemas.find(schema => schema.type === step.type && schema.version === step.version);
                if (!schema) {
                    throw new Error(`Cannot find schema for the provided step type '${step.type}' and version '${step.version}'.`);
                }
                return schema;
            });
            const stepRequests = steps.map((step, index) => ({
                ...step,
                order: index + 1
            }));
            // get Zero Trust access token
            const zeroTrustAccessToken = await this.zeroTrustService.getJwt();
            const effectiveRunTimeout = this.calculateRunTimeoutInSeconds(isScheduled, effectiveScheduleDateTime, hostsWithTimezone);
            const effectiveTaskTimeout = taskTimeout !== null && taskTimeout !== void 0 ? taskTimeout : this.calculateTaskTimeoutInSeconds(steps, stepSchemas, effectiveRunTimeout);
            const recurrenceOptionsDto = recurrenceOptions
                ? await this.getRecurrenceOptionsDto(recurrenceOptions, modalText)
                : undefined;
            const jobSignatureVersion = recurrenceOptions ? JobSignatureVersion.RecurringJobs : JobSignatureVersion.Files;
            // sign job
            const jobSignatureAsBytes = await this.zeroTrustService.signJob(jobSignatureVersion, stepRequests, stepSchemas, effectiveScheduleDateTime, effectiveRunTimeout, effectiveTaskTimeout, recurrenceOptionsDto === null || recurrenceOptionsDto === void 0 ? void 0 : recurrenceOptionsDto.id, modalText);
            // create and send request
            const request = {
                displayName,
                hosts: hostsWithTimezone,
                steps: stepRequests,
                runAt: effectiveScheduleDateTime,
                isScheduled,
                schedulerTimeZone: browserTimeZone,
                runTimeout: convertSecondsToTimeSpan(effectiveRunTimeout),
                taskTimeout: convertSecondsToTimeSpan(effectiveTaskTimeout),
                platform,
                signature: arrayBufferToBase64(jobSignatureAsBytes),
                signatureVersion: jobSignatureVersion,
                zeroTrustAccessToken,
                app,
                recurrenceOptions: recurrenceOptionsDto
            };
            return await this.http.post('automation/runs', request);
        }
        catch (error) {
            this.logger.logError(`Remote Execution service run job: ${error}`);
            if (error instanceof Response) {
                await this.handleWellKnownErrors(error);
            }
            throw error;
        }
    }
    async handleWellKnownErrors(response) {
        if (!isJsonResponse(response)) {
            return;
        }
        const body = await response.json();
        switch (body.type) {
            case 'RunAtOutOfRangeException': {
                const systemUtcNowDateTime = DateTime.fromISO(body.systemUtcNow);
                const runAtDateTime = DateTime.fromJSDate(new Date());
                const timeDifferenceBetweenServerAndClientInMinutes = Math.round(systemUtcNowDateTime.diff(runAtDateTime, ['minutes']).minutes);
                if (timeDifferenceBetweenServerAndClientInMinutes >= 5) {
                    throw new TimeIsOutOfSyncError(timeDifferenceBetweenServerAndClientInMinutes);
                }
                throw new RunAtOutOfRangeError();
            }
            default:
                break;
        }
    }
    /**
     * Cancels a Remote Execution job.
     * @param id - The id of the run.
     * @remarks The run can only be cancelled on the device if it hasn't finished yet. If the job is already running, only pending steps will be cancelled.
     * */
    async cancelRun(id) {
        return this.http.post(`automation/runs/${id}/cancel`, { runId: id });
    }
    /**
     * Deactivates a Remote Execution recurring job.
     * @param id - The id of the recurring run.
     * @param recurrenceOptions -  The parameters related to the recurrence.
     * @param modalText - The text Zero Trust sign flow modal displays to the user.
     */
    async deactivateRecurringRun(id, recurrenceOptions, modalText) {
        const recurrenceSignatureAsBytes = await this.zeroTrustService.signJobRecurrenceOptions(recurrenceOptions.recurrencePattern, recurrenceOptions.recurrenceStartDate, recurrenceOptions.recurrenceEndDate, recurrenceOptions.state, modalText);
        const recurrenceOptionsDto = {
            id: id,
            recurrencePattern: recurrenceOptions.recurrencePattern,
            recurrenceStartDate: recurrenceOptions.recurrenceStartDate,
            recurrenceEndDate: recurrenceOptions.recurrenceEndDate,
            state: RecurrenceState.Inactive,
            signature: arrayBufferToBase64(recurrenceSignatureAsBytes),
            signatureVersion: 1
        };
        return this.http.post(`automation/recurring-runs/${id}/deactivate`, recurrenceOptionsDto);
    }
    /**
     * Gets all step collections.
     * @returns The step collections.
     * @remarks Step collections are saved steps (array of `TaskStep`).
     * */
    async getStepCollections() {
        return this.http.get('tasks');
    }
    /**
     * @deprecated Use `RemoteExecutionLiveUpdatesService` on function with `HostRunChanged` hub method name.
     * */
    async getHostRuns(runId) {
        const hostRunDtos = await this.http.get(`automation/runs/${runId}/hosts`);
        return hostRunDtos.map(dto => ({
            ...dto,
            startedAt: dto.startedAt ? new Date(dto.startedAt) : undefined,
            finishedAt: dto.finishedAt ? new Date(dto.finishedAt) : undefined,
            scheduledAt: dto.scheduledAt ? new Date(dto.scheduledAt) : undefined
        }));
    }
    async getStepSchemas() {
        return this.http.get('automation/schemas');
    }
    calculateRunTimeoutInSeconds(scheduled, effectiveScheduleDateTime, hosts) {
        if (!scheduled) {
            return this.defaultRunTimeout;
        }
        const deviceTimeZoneOffsets = hosts.map(device => DateTime.fromJSDate(effectiveScheduleDateTime).setZone(device.timeZone).offset);
        const offsetDeltaSeconds = deviceTimeZoneOffsets.length
            ? (Math.max(...deviceTimeZoneOffsets) - Math.min(...deviceTimeZoneOffsets)) * 60
            : 0;
        return this.defaultRunTimeout + offsetDeltaSeconds;
    }
    calculateTaskTimeoutInSeconds(steps, stepSchemas, runTimeout) {
        let taskTimeout = stepSchemas.reduce((accumulator, schema) => { var _a; return accumulator + ((_a = schema.timeout) !== null && _a !== void 0 ? _a : 0); }, this.defaultTaskTimeout);
        // handle wait steps if any
        const waitSteps = steps.filter(step => step.type === 'wait');
        if (waitSteps.length) {
            const waitingTimeSumInSeconds = waitSteps.reduce((accumulator, waitStep) => {
                var _a, _b;
                const value = waitStep.version === 1
                    ? Math.floor(((_a = waitStep.arguments[0]) === null || _a === void 0 ? void 0 : _a.value) / 1000)
                    : (_b = waitStep.arguments[0]) === null || _b === void 0 ? void 0 : _b.value;
                return accumulator + value;
            }, 0);
            taskTimeout += waitingTimeSumInSeconds;
        }
        // cap to runTimeout value (24 hours + host run timezone difference)
        return taskTimeout > runTimeout ? runTimeout : taskTimeout;
    }
    async getRecurrenceOptionsDto(recurrenceOptions, modalText) {
        const recurringJobId = window.crypto.randomUUID();
        const recurrenceSignatureAsBytes = await this.zeroTrustService.signJobRecurrenceOptions(recurrenceOptions.recurrencePattern, recurrenceOptions.recurrenceStartDate, recurrenceOptions.recurrenceEndDate, recurrenceOptions.state, modalText);
        return {
            id: recurringJobId,
            recurrencePattern: recurrenceOptions.recurrencePattern,
            recurrenceStartDate: recurrenceOptions.recurrenceStartDate,
            recurrenceEndDate: recurrenceOptions.recurrenceEndDate,
            state: RecurrenceState.Active,
            signature: arrayBufferToBase64(recurrenceSignatureAsBytes),
            signatureVersion: 1
        };
    }
}
